import { pieArcLabelClasses, PieChart } from '@mui/x-charts';
import { colors } from '../Incidents/charts/colorsPalettes';
import { Box, Typography } from '@mui/material';

const sizing = {
  margin: { right: 5 },
  width: 400,
  height: 400,
  legend: { hidden: true },
};

// Converts the string "mm:ss" to seconds
const parseTimeToSeconds = (timeStr) => {
  if (!timeStr || !timeStr.includes(':')) return NaN;

  const [mm, ss] = timeStr.split(':').map(Number);
  if (isNaN(mm) || isNaN(ss)) return NaN;

  return mm * 60 + ss;
};

// Calculation of distribution by time ranges
const getTimeFillDistribution = (data) => {
  if (!data) {
    return {
      labels: [],
      percents: [],
    };
  }

  const secondsArray = data
    .map((d) => parseTimeToSeconds(d.time_to_fill))
    .filter((sec) => !isNaN(sec));

  const total = secondsArray.length;
  if (total === 0) return { labels: [], percents: [] };

  const maxSec = Math.max(...secondsArray);

  const binLimits = [];
  const maxMinutes = Math.ceil(maxSec / 60) + 1;
  for (let i = 1; i <= maxMinutes; i += 2) {
    binLimits.push(i);
  }

  const binLabels = binLimits.map((min) => `${min} min`);
  const binCounts = new Array(binLimits.length).fill(0);

  secondsArray.forEach((sec) => {
    binLimits.forEach((limitMin, index) => {
      const lowerLimit = index === 0 ? 0 : binLimits[index - 1] * 60;
      const upperLimit = limitMin * 60;
      if (sec > lowerLimit && sec <= upperLimit) {
        binCounts[index]++;
        return;
      }
    });
  });

  const binPercents = binCounts.map((count) =>
    parseFloat(((count / total) * 100).toFixed(2))
  );

  return {
    labels: binLabels,
    percents: binPercents,
  };
};

function FillTimePieChart({ data }) {
  const { labels, percents } = getTimeFillDistribution(data);

  // Convert to format for PieChart: { id, value, label }
  const pieData = labels.map((label, index) => ({
    id: index,
    value: percents[index],
    label,
  }));

  const getArcLabel = (params) => {
    const percent = params.value;
    return percent >= 5 ? percent : '';
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box>
        <PieChart
          colors={colors}
          series={[
            {
              innerRadius: 120,
              outerRadius: 180,
              data: pieData,
              arcLabel: getArcLabel,
            },
          ]}
          sx={{
            [`& .${pieArcLabelClasses.root}`]: {
              fill: 'white',
              fontSize: 14,
            },
          }}
          {...sizing}
        />
      </Box>
      <Box
        display="flex"
        justifyContent="start"
        flexWrap="wrap"
        sx={{ width: 'fit-content' }}
      >
        {[
          pieData.slice(0, Math.ceil(pieData.length / 2)),
          pieData.slice(Math.ceil(pieData.length / 2)),
        ].map((columnData, colIndex) => (
          <Box key={colIndex} mx={2}>
            {columnData.map((item, index) => {
              const globalIndex = colIndex * Math.ceil(data.length / 2) + index;
              return (
                <Box key={item.label} display="flex" alignItems="center" mb={1}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      backgroundColor: colors[globalIndex % colors.length],
                      borderRadius: '30%',
                      marginRight: 1,
                    }}
                  />
                  <Typography variant="body2" color="text.primary">
                    {item.label}: {item.value + '%'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default FillTimePieChart;
